import React, { useState } from 'react';
import type { TipoFinais } from '../../../../../@types/IPA/PrecoPsicologico';

interface ConfigTipo {
  placeholder: string;
  maxDigitos: number;
  maxValor: number;
  dica: string;
}

const CONFIG: Record<TipoFinais, ConfigTipo> = {
  1: { placeholder: '0',    maxDigitos: 1, maxValor: 9,     dica: 'Único dígito entre 0 e 9' },
  2: { placeholder: '00',   maxDigitos: 2, maxValor: 99,    dica: 'Dois dígitos entre 00 e 99' },
  3: { placeholder: '0,00', maxDigitos: 4, maxValor: 9.99,  dica: 'Entre 0,00 e 9,99' },
  4: { placeholder: '00,00',maxDigitos: 5, maxValor: 99.99, dica: 'Entre 00,00 e 99,99' },
  5: { placeholder: '000,00',maxDigitos: 6,maxValor: 999.99,dica: 'Entre 000,00 e 999,99' },
};

function valorParaRaw(valor: string, tipo: TipoFinais): string {
  if (!valor) return '';
  if (tipo <= 2) return valor.replace(/\D/g, '');
  const num = parseFloat(valor.replace(',', '.')) || 0;
  const centavos = Math.round(num * 100);
  return centavos > 0 ? String(centavos) : '';
}

function rawParaFormatado(raw: string, tipo: TipoFinais): string {
  if (!raw) return '';
  if (tipo <= 2) return raw;
  const centavos = parseInt(raw, 10) || 0;
  return (centavos / 100).toFixed(2).replace('.', ',');
}

interface InputFinalProps {
  value: string;
  tipo: TipoFinais;
  onMudar: (valor: string) => void;
}

function InputFinal({ value, tipo, onMudar }: InputFinalProps) {
  const config = CONFIG[tipo];
  const [raw, setRaw] = useState(() => valorParaRaw(value, tipo));
  const ultimoValorRef = React.useRef(value);

  if (value !== ultimoValorRef.current) {
    ultimoValorRef.current = value;
    const novoRaw = valorParaRaw(value, tipo);
    if (novoRaw !== raw) setRaw(novoRaw);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Tab' || e.key === 'Escape') return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const novoRaw = raw.slice(0, -1);
      setRaw(novoRaw);
      const formatado = rawParaFormatado(novoRaw, tipo);
      ultimoValorRef.current = formatado;
      onMudar(formatado);
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      setRaw('');
      ultimoValorRef.current = '';
      onMudar('');
      return;
    }

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const novoRaw = (raw + e.key).slice(0, config.maxDigitos);
      const formatado = rawParaFormatado(novoRaw, tipo);
      const num = parseFloat(formatado.replace(',', '.')) || 0;
      if (num > config.maxValor) return;
      setRaw(novoRaw);
      ultimoValorRef.current = formatado;
      onMudar(formatado);
      return;
    }

    if (e.key.length === 1) e.preventDefault();
  }

  return (
    <input
      type="text"
      className="finais__input"
      value={value}
      onKeyDown={handleKeyDown}
      onChange={() => {}}
      placeholder={config.placeholder}
    />
  );
}

interface Props {
  tipo: TipoFinais;
  value: string[];
  onChange: (finais: string[]) => void;
  error?: string;
  disabled?: boolean;
}

export function FinaisPermitidos({ tipo, value, onChange, error, disabled }: Props) {
  const config = CONFIG[tipo];
  const passo = tipo <= 2 ? 1 : 0.01;

  function atualizar(index: number, novoValor: string) {
    const atualizados = [...value];
    atualizados[index] = novoValor;
    onChange(atualizados);
  }

  function incrementar(index: number) {
    const atual = parseFloat((value[index] || '0').replace(',', '.')) || 0;
    const incrementado = Math.min(atual + passo, config.maxValor);
    const atualizados = [...value];
    if (tipo <= 2) {
      atualizados[index] = String(Math.round(incrementado)).padStart(tipo, '0');
    } else {
      atualizados[index] = incrementado.toFixed(2).replace('.', ',');
    }
    onChange(atualizados);
  }

  function decrementar(index: number) {
    const atual = parseFloat((value[index] || '0').replace(',', '.')) || 0;
    const decrementado = Math.max(atual - passo, 0);
    const atualizados = [...value];
    if (tipo <= 2) {
      atualizados[index] = String(Math.round(decrementado)).padStart(tipo, '0');
    } else {
      atualizados[index] = decrementado.toFixed(2).replace('.', ',');
    }
    onChange(atualizados);
  }

  function adicionar() {
    onChange([...value, '']);
  }

  function remover(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function temDuplicata(val: string, index: number) {
    if (!val) return false;
    return value.some((v, i) => i !== index && v === val);
  }

  return (
    <div className="finais">
      <div className="finais__header">
        <span className="finais__titulo">Final</span>
        <span className="finais__subtitulo">Escolha qual arredondamento para essa faixa</span>
      </div>
      <div className="finais__entradas">
        {value.map((final, index) => (
          <div key={index} className="finais__entrada">
            <div className="finais__linha">
              <div className={`finais__input-wrap${temDuplicata(final, index) ? ' finais__input-wrap--erro' : ''}`}>
                <div className="finais__input-inner">
                  <InputFinal
                    key={`${tipo}-${index}`}
                    value={final}
                    tipo={tipo}
                    onMudar={v => atualizar(index, v)}
                  />
                </div>
                <div className="finais__setas">
                  <button
                    type="button"
                    tabIndex={-1}
                    className="finais__seta"
                    disabled={disabled}
                    onClick={() => incrementar(index)}
                    aria-label="Incrementar"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 14l5-5 5 5z"/></svg>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="finais__seta"
                    disabled={disabled}
                    onClick={() => decrementar(index)}
                    aria-label="Decrementar"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                  </button>
                </div>
              </div>
              {index === 0 ? (
                <span className="finais__dica">{config.dica}</span>
              ) : (
                <button
                  type="button"
                  className="finais__remover"
                  disabled={disabled}
                  onClick={() => remover(index)}
                  aria-label="Remover final"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              )}
            </div>
            {temDuplicata(final, index) && (
              <span className="finais__erro">Final repetido</span>
            )}
          </div>
        ))}
        <button
          type="button"
          className="finais__adicionar-link"
          disabled={disabled}
          onClick={adicionar}
        >
          + Adicionar mais um final
        </button>
      </div>
      {error && <span className="finais__erro">{error}</span>}
    </div>
  );
}
